import React, {Component} from "react";
import {createPortal} from "react-dom";
import {Visible} from "./visible";
import {_t} from '../../../js/translation';
import {HintPortalContext} from "./hint.portal.context";

// Matches $mob: 767px / break-min($mob) in _hint.scss — above this width the block is
// desktop-anchored (position: absolute near the icon); below it, it's a full-width bottom sheet.
const DESKTOP_BREAKPOINT = 767;

export default class Hint extends Component{
    static contextType = HintPortalContext;

    constructor(props) {
        super(props);

        this.state = {
            isVisible: false,
            // Only computed/used in portal mode — viewport-relative rect of the icon, captured
            // when the hint opens, used to position the portaled block via fixed coordinates
            // instead of relying on a CSS ancestor that may clip or transform it.
            iconRect: null,
        }
    }

    // Explicit `portal` prop always wins; otherwise inherit from HintPortalContext so a page/panel
    // can opt every Hint underneath it into portal mode without prop-drilling through every field.
    isPortal = () => this.props.portal !== undefined ? this.props.portal : this.context;

    componentDidMount() {
        document.addEventListener('click', this.clickHandler);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.clickHandler);
        this.detachPortalListeners();
    }

    attachPortalListeners = () => {
        if (!this.isPortal()) return;
        window.addEventListener('scroll', this.updatePosition, true);
        window.addEventListener('resize', this.updatePosition);
    };

    detachPortalListeners = () => {
        window.removeEventListener('scroll', this.updatePosition, true);
        window.removeEventListener('resize', this.updatePosition);
    };

    updatePosition = () => {
        if (!this.node) return;
        this.setState({ iconRect: this.node.getBoundingClientRect() });
    };

    showHint = () => {
        const iconRect = this.isPortal() && this.node ? this.node.getBoundingClientRect() : null;
        this.setState({ isVisible: true, iconRect });
        this.attachPortalListeners();
    };

    hideHint = () => {
        this.detachPortalListeners();
        this.setState({
            isVisible: false
        });
    };

    clickHandler = (e) => {
        const clickedIcon = this.node && this.node.contains(e.target);
        const clickedBlock = this.blockNode && this.blockNode.contains(e.target);

        if (clickedIcon) {
            this.state.isVisible ? this.hideHint() : this.showHint();
            return;
        }

        if (clickedBlock) {
            // Click inside the portaled block (e.g. selecting text) isn't a click on the icon,
            // but it's also not "outside" — only real outside clicks should close the hint.
            return;
        }

        this.hideHint();
    };

    handleMouseEnter = () => {
        if (this.isPortal() && !this.state.isVisible) this.showHint();
    };

    handleMouseLeave = () => {
        if (this.isPortal() && this.state.isVisible) this.hideHint();
    };

    renderBlock() {
        const { title, text, customHtml } = this.props;
        const { isVisible, iconRect } = this.state;
        const portal = this.isPortal();
        const isDesktop = typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT;

        let style;
        if (portal && isDesktop && iconRect) {
            const width = 200;
            const left = Math.min(
                Math.max(8, iconRect.left - 100),
                window.innerWidth - width - 8
            );
            style = { position: 'fixed', top: iconRect.top + 10, left, bottom: 'auto' };
        }

        const block = (
            <div
                ref={node => this.blockNode = node}
                className={'hint-component__block' + (isVisible ? ' hint-component__block-show' : '')}
                style={style}
            >
                <Visible visible={title}>
                    <div className="hint-component__header">
                        <h5 className="hint-component__title">
                            {_t(title)}
                        </h5>
                    </div>
                </Visible>

                <div className="hint-component__body">
                    <div className="hint-component__content">
                        {text && _t(text)}
                        {customHtml && customHtml}
                    </div>
                </div>
            </div>
        );

        if (portal) {
            const root = document.getElementById('modal_root');
            if (root) return createPortal(block, root);
        }

        return block;
    }

    render() {
        // Only attach mouseenter/leave when they'd actually do something (portal mode replaces
        // the CSS :hover rule, which itself only ever applied at this same desktop breakpoint).
        // Attaching them unconditionally — even as a no-op handler — makes mobile Safari/Chrome
        // treat the first tap as a hover-simulation and swallow it, requiring a second tap to
        // register the real click. Not needed on mobile anyway: touch has no hover concept, and
        // the original CSS :hover never applied below this breakpoint either.
        const isDesktop = typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT;
        const hoverHandlers = this.isPortal() && isDesktop
            ? { onMouseEnter: this.handleMouseEnter, onMouseLeave: this.handleMouseLeave }
            : {};

        return(
            <i type={'button'} ref={node => this.node = node}
               className={'hint-component hint-component__icon fal fa-' + (this.props.icon || "info-square")}
               {...hoverHandlers}
            >
                {this.renderBlock()}
            </i>
        )
    }
}